import { useCallback, useEffect, useRef, useState } from 'react'

function roundValues(_rect) {
  const rect = {
    ..._rect,
  }
  for (const key of Object.keys(rect)) {
    rect[key] = Math.round(rect[key])
  }
  return rect
}

function shallowDiff(prev, next) {
  if (prev != null && next != null) {
    for (const key of Object.keys(next)) {
      if (prev[key] !== next[key]) {
        return true
      }
    }
  } else if (prev !== next) {
    return true
  }
  return false
}

/**
 * useTextSelection
 * hook to get information about the current text selection
 * Modified from https://github.com/juliankrispel/use-text-selection
 */

export function useTextSelection() {
  const [clientRect, setRect] = useState()
  const [isCollapsed, setIsCollapsed] = useState()
  const [textContent, setText] = useState()
  const rangeRef = useRef()

  const reset = useCallback(() => {
    setRect(undefined)
    setIsCollapsed(undefined)
    setText(undefined)
  }, [])

  const handler = useCallback(() => {
    let newRect
    const selection = window.getSelection()

    if (selection == null || !selection.rangeCount) {
      reset()
      return
    }
    const range = selection.getRangeAt(0)
    rangeRef.current = range

    // check if selection is inside elements considered to be read aloud
    if (
      (!selection.anchorNode.parentNode.closest('[data-readaloud]') &&
        !selection.anchorNode.parentNode.closest('[data-readaloud-title]')) ||
      (!selection.focusNode.parentNode.closest('[data-readaloud]') &&
        !selection.focusNode.parentNode.closest('[data-readaloud-title]'))
    ) {
      reset()
      return
    }

    if (range == null) {
      reset()
      return
    }

    const contents = range.cloneContents()

    if (contents.textContent != null) setText(contents.textContent)

    const rects = range.getClientRects()

    if (rects.length === 0 && range.commonAncestorContainer != null) {
      const el = range.commonAncestorContainer
      newRect = roundValues(el.getBoundingClientRect().toJSON())
    } else {
      if (rects.length < 1) return
      newRect = roundValues(rects[0].toJSON())
    }
    setRect(oldRect => {
      if (shallowDiff(oldRect, newRect)) {
        return newRect
      }
      return oldRect
    })
    setIsCollapsed(range.collapsed)
  })

  useEffect(() => {
    document.addEventListener('pointerup', handler)
    document.addEventListener('keydown', handler)
    document.addEventListener('keyup', handler)
    window.addEventListener('resize', handler)

    return () => {
      document.removeEventListener('pointerup', handler)
      document.removeEventListener('keydown', handler)
      document.removeEventListener('keyup', handler)
      window.removeEventListener('resize', handler)
    }
  }, [])

  return {
    clientRect,
    isCollapsed,
    textContent,
    rangeRef,
  }
}



// WEBPACK FOOTER //
// ./src/hooks/useTextSelection.js